// RecipeMaster.js — Builds on the existing ItemStoreMapping backend
// (/home/AddMapping, /home/GetMappings, /home/ToggleMapping) but reframes it
// as a recipe builder: pick a menu item, see ALL ingredients in one view,
// add/remove multiple ingredients at once, see live recipe cost.
//
// Backend changes required: none. The DeductStock flow that already fires from
// SaveOrder consumes these mappings automatically — what we save here is what
// the order pipeline reads.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  MenuItem,
  Autocomplete,
  Chip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import config from '../../config';

const UNITS = ['ml', 'ltr', 'gm', 'kg', 'pcs', 'nos', 'bottle'];

// Each "draft" ingredient the user is composing before save. Once persisted
// (via /home/AddMapping) it shows up in the `existing` table on next fetch.
const EMPTY_INGREDIENT = {
  storeItem: null,
  storeItemId: null,
  qty: '',
  unit: 'ml',
};

export default function RecipeMaster() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const Companycurrency = company?.currency ?? '';

  const [menuItems, setMenuItems] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [storeItems, setStoreItems] = useState([]);
  const [allMappings, setAllMappings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Two modes:
  //   'new'  — create a brand new menu item AND its recipe from this page.
  //            Recipe IS the menu item — shows up in Create Order under its
  //            chosen category once saved.
  //   'edit' — pick an existing menu item and modify its recipe ingredients.
  const [mode, setMode] = useState('new');

  // "Edit existing" state — what we're building a recipe FOR. Can be either
  // a parent menu item, or a specific variant (subcategory).
  const [scope, setScope] = useState('menu'); // 'menu' | 'variant'
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');

  // "New recipe" state — captures everything needed to create the underlying
  // RestItem (menu item) + the recipe metadata in one save.
  const [newRecipe, setNewRecipe] = useState({
    name: '',
    categoryId: '',
    sellingPrice: '',
    gstPercent: '',
    department: '',
    hsnCode: '',
  });

  // Recipe's own name — independent of the menu item (e.g. "Old Monk 30ml Peg"
  // for the "Whiskey" menu item). Persists on each ingredient row so the
  // recipe identity travels with its ingredients.
  const [recipeName, setRecipeName] = useState('');

  // Draft ingredients the user is adding now (not yet saved).
  const [draftIngredients, setDraftIngredients] = useState([
    { ...EMPTY_INGREDIENT },
  ]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ─── Initial load ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      if (!company?.id) return;
      setLoading(true);
      try {
        const [menuRes, storeRes, mapRes, subRes, catRes, deptRes] =
          await Promise.all([
            axios.get(
              `${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
            axios.get(
              `${config.API_BASE_URL}/home/GetStoreItems/${company.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
            axios.get(`${config.API_BASE_URL}/home/GetMappings/${company.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(
              `${config.API_BASE_URL}/home/GetSubcategories/${company.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
            // Categories + departments — required by the "New Recipe" form so a
            // recipe can be mapped to a category (so it appears in Create Order)
            // and routed to a printer (so KOTs print correctly).
            axios.get(
              `${config.API_BASE_URL}/Home/GetAllCategory/${company.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
            axios.get(
              `${config.API_BASE_URL}/Home/GetDepartments/${company.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
          ]);
        setMenuItems(menuRes.data || []);
        setStoreItems(storeRes.data || []);
        setAllMappings(mapRes.data || []);
        setSubcategories(subRes.data || []);
        setCategories(catRes.data || []);
        const deptData = deptRes.data || [];
        setDepartments(deptData);
        // Default the printer to the first one so the form has a usable value.
        if (deptData[0]) {
          setNewRecipe((r) => ({
            ...r,
            department:
              deptData[0].printername || deptData[0].Printername || '',
          }));
        }
      } catch (err) {
        console.error('Recipe load failed:', err);
        notify('Failed to load recipe data', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchMappings = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetMappings/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAllMappings(res.data || []);
    } catch {
      // notify silently — don't override a primary action's message
    }
  };

  // ─── Lookups ──────────────────────────────────────────────────────────
  const storeById = useMemo(() => {
    const m = {};
    storeItems.forEach((s) => {
      m[s.id ?? s.Id] = s;
    });
    return m;
  }, [storeItems]);

  const menuById = useMemo(() => {
    const m = {};
    menuItems.forEach((i) => {
      m[i.id] = i;
    });
    return m;
  }, [menuItems]);

  const subById = useMemo(() => {
    const m = {};
    subcategories.forEach((s) => {
      m[s.id] = s;
    });
    return m;
  }, [subcategories]);

  // Existing recipe = mappings for the picked menu item / variant.
  const existingRecipe = useMemo(() => {
    if (scope === 'menu' && selectedMenuId) {
      return allMappings.filter(
        (m) =>
          !m.isSubcategory &&
          String(m.itemId) === String(selectedMenuId) &&
          (m.subcategoryId === 0 || m.subcategoryId == null),
      );
    }
    if (scope === 'variant' && selectedVariantId) {
      return allMappings.filter(
        (m) =>
          (m.isSubcategory &&
            String(m.subcategoryId) === String(selectedVariantId)) ||
          // Legacy rows where the variant id was stored on itemId
          (m.isSubcategory &&
            String(m.itemId) === String(selectedVariantId) &&
            (m.subcategoryId === 0 || m.subcategoryId == null)),
      );
    }
    return [];
  }, [scope, selectedMenuId, selectedVariantId, allMappings]);

  // Auto-load the saved recipe name from any existing ingredient row so the
  // user sees their previous recipe name when they re-open the recipe. Falls
  // back to a derived default ("<menu item> Recipe") for fresh recipes so
  // there's always SOME text — easier to discover/edit than an empty field.
  useEffect(() => {
    const saved = existingRecipe.find(
      (m) => m.recipeName && m.recipeName.trim(),
    )?.recipeName;
    if (saved) {
      setRecipeName(saved);
    } else if (scope === 'menu' && menuById[selectedMenuId]) {
      setRecipeName(`${menuById[selectedMenuId].name} Recipe`);
    } else if (scope === 'variant' && subById[selectedVariantId]) {
      const v = subById[selectedVariantId];
      setRecipeName(
        `${v.subcategoryName || v.SubcategoryName || 'Variant'} Recipe`,
      );
    } else {
      setRecipeName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenuId, selectedVariantId, scope, allMappings.length]);

  // Recipe cost — best-effort using StoreMasterQuick's last known stock
  // (assumes 1 unit of stock currency-per-unit ≈ purchase rate; if your
  // schema tracks a per-unit cost field we'll use it instead).
  const recipeCost = useMemo(() => {
    return existingRecipe.reduce((sum, ing) => {
      const store = storeById[ing.storeItemId];
      if (!store) return sum;
      const ratePerUnit =
        Number(store.lastPurchaseRate ?? store.LastPurchaseRate ?? 0) ||
        // Fallback heuristic: derive a rough rate from purchases if available
        0;
      return sum + Number(ing.consumptionQty || 0) * ratePerUnit;
    }, 0);
  }, [existingRecipe, storeById]);

  // ─── Draft handlers ───────────────────────────────────────────────────
  const updateDraft = (i, patch) => {
    const next = [...draftIngredients];
    next[i] = { ...next[i], ...patch };
    setDraftIngredients(next);
  };
  const addDraftRow = () =>
    setDraftIngredients([...draftIngredients, { ...EMPTY_INGREDIENT }]);
  const removeDraftRow = (i) => {
    if (draftIngredients.length === 1) {
      setDraftIngredients([{ ...EMPTY_INGREDIENT }]);
      return;
    }
    setDraftIngredients(draftIngredients.filter((_, idx) => idx !== i));
  };

  // ─── Reset draft when scope changes ───────────────────────────────────
  useEffect(() => {
    setDraftIngredients([{ ...EMPTY_INGREDIENT }]);
  }, [scope, selectedMenuId, selectedVariantId]);

  // ─── Save ─────────────────────────────────────────────────────────────
  const saveRecipe = async () => {
    const validDrafts = draftIngredients.filter(
      (d) => d.storeItemId && parseFloat(d.qty) > 0,
    );
    if (validDrafts.length === 0) {
      notify(
        'Add at least one ingredient with a store item and quantity',
        'warning',
      );
      return;
    }

    // Resolve target ids for AddMapping shape. Two paths:
    //   • mode='new'  — create a brand new RestItem first, then save mappings
    //     against the new item id (always non-variant).
    //   • mode='edit' — use existing menu item / variant the user picked.
    let targetItemId, targetSubId, isSub, finalRecipeName;
    setSaving(true);
    try {
      if (mode === 'new') {
        // Validate the new-recipe form
        if (!newRecipe.name.trim()) {
          notify('Recipe name is required', 'warning');
          setSaving(false);
          return;
        }
        if (!newRecipe.categoryId) {
          notify(
            'Pick a category so the recipe shows up in Create Order',
            'warning',
          );
          setSaving(false);
          return;
        }
        if (
          !newRecipe.sellingPrice ||
          parseFloat(newRecipe.sellingPrice) <= 0
        ) {
          notify('Selling price must be greater than 0', 'warning');
          setSaving(false);
          return;
        }
        if (!newRecipe.department) {
          notify('Pick a department/printer', 'warning');
          setSaving(false);
          return;
        }

        // Block duplicate menu items — if a RestItem with the same name in
        // the same category already exists, bail early with a clear message.
        const duplicateExisting = menuItems.find(
          (m) =>
            (m.name || '').trim().toLowerCase() ===
              newRecipe.name.trim().toLowerCase() &&
            String(m.cateid ?? m.categoryId) === String(newRecipe.categoryId),
        );
        if (duplicateExisting) {
          notify(
            `A menu item "${newRecipe.name}" already exists in this category. Switch to Edit Existing mode.`,
            'warning',
          );
          setSaving(false);
          return;
        }

        // 1) Create the underlying menu item (RestItem)
        const createPayload = {
          categoryId: parseInt(newRecipe.categoryId, 10),
          itemName: newRecipe.name.trim(),
          hsnCode: newRecipe.hsnCode?.trim() || '',
          itemPrice: parseFloat(newRecipe.sellingPrice) || 0,
          gst: parseFloat(newRecipe.gstPercent) || 0,
          vatRate: 0,
          isVatApplicable: false,
          department: newRecipe.department,
          companyid: company?.id,
        };
        await axios.post(
          `${config.API_BASE_URL}/Home/CreateItem`,
          createPayload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        // 2) Refresh master items + find the row we just created
        const refresh = await axios.get(
          `${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const refreshed = refresh.data || [];
        setMenuItems(refreshed);
        const created = refreshed.find(
          (m) =>
            (m.name || '').trim().toLowerCase() ===
              newRecipe.name.trim().toLowerCase() &&
            String(m.cateid ?? m.categoryId) === String(newRecipe.categoryId),
        );
        if (!created) {
          notify(
            'Menu item created but could not be located. Refresh and try again.',
            'error',
          );
          setSaving(false);
          return;
        }
        targetItemId = created.id;
        targetSubId = 0;
        isSub = false;
        finalRecipeName = newRecipe.name.trim();
      } else {
        // mode === 'edit'
        if (scope === 'menu' && !selectedMenuId) {
          notify('Pick a menu item first', 'warning');
          setSaving(false);
          return;
        }
        if (scope === 'variant' && !selectedVariantId) {
          notify('Pick a variant first', 'warning');
          setSaving(false);
          return;
        }
        if (scope === 'menu') {
          targetItemId = parseInt(selectedMenuId, 10);
          targetSubId = 0;
          isSub = false;
        } else {
          const variant = subById[selectedVariantId];
          const parentId =
            variant?.parentItemId ??
            variant?.ParentItemId ??
            variant?.itemId ??
            null;
          if (parentId == null) {
            notify(
              'This variant has no linked parent menu item — fix it in Subcategory master first.',
              'error',
            );
            setSaving(false);
            return;
          }
          targetItemId = parseInt(parentId, 10);
          targetSubId = parseInt(selectedVariantId, 10);
          isSub = true;
        }
        finalRecipeName = recipeName.trim() || null;
      }
    } catch (createErr) {
      console.error(
        'Recipe create failed:',
        createErr?.response?.data || createErr,
      );
      notify(
        createErr.response?.data?.message ||
          createErr.message ||
          'Failed to create menu item',
        'error',
      );
      setSaving(false);
      return;
    }

    // ─── Save ingredient mappings ─────────────────────────────────────
    let savedCount = 0;
    let failedCount = 0;
    try {
      for (const draft of validDrafts) {
        try {
          const payload = {
            companyId: parseInt(company.id, 10),
            itemId: targetItemId,
            subcategoryId: targetSubId,
            isSubcategory: isSub,
            storeItemId: parseInt(draft.storeItemId, 10),
            consumptionQty: parseFloat(draft.qty),
            unit: (draft.unit || 'ml').trim(),
            // Recipe name travels with every ingredient row — same name across
            // the whole recipe so it stays consistent in the GetMappings list.
            // In 'new' mode this is the recipe/menu-item name we just created;
            // in 'edit' mode it's whatever the user typed in the recipe name field.
            recipeName: finalRecipeName,
            activeStatus: true,
          };
          await axios.post(`${config.API_BASE_URL}/home/AddMapping`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          savedCount++;
        } catch (rowErr) {
          console.error(
            'Ingredient save failed:',
            rowErr?.response?.data || rowErr,
          );
          failedCount++;
        }
      }

      if (savedCount > 0) {
        const successMsg =
          mode === 'new'
            ? `Recipe "${finalRecipeName}" created with ${savedCount} ingredient${savedCount > 1 ? 's' : ''}. It's now visible in Create Order.`
            : `${savedCount} ingredient${savedCount > 1 ? 's' : ''} added${failedCount > 0 ? ` (${failedCount} failed)` : ''}`;
        notify(successMsg, failedCount > 0 ? 'warning' : 'success');
        setDraftIngredients([{ ...EMPTY_INGREDIENT }]);
        refetchMappings();
        // In "new" mode also reset the recipe form so the user can add the
        // next recipe without re-typing the company/department defaults.
        if (mode === 'new') {
          setNewRecipe((r) => ({
            ...r,
            name: '',
            sellingPrice: '',
            gstPercent: '',
            hsnCode: '',
            // categoryId + department kept — user often adds many recipes to
            // the same category in one sitting.
          }));
        }
      } else {
        notify('Failed to save any ingredient', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── Toggle (soft-delete) ──────────────────────────────────────────────
  const toggleIngredient = async (mappingId) => {
    try {
      await axios.get(
        `${config.API_BASE_URL}/home/ToggleMapping/${mappingId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      refetchMappings();
      notify('Ingredient toggled');
    } catch (err) {
      notify('Toggle failed', 'error');
    }
  };

  // ─── Stock warnings — quick sanity check on recipe vs available stock ──
  // For each existing ingredient, flag if its consumption qty already exceeds
  // current stock (i.e. ordering one of this menu item would zero out stock).
  const stockWarnings = useMemo(() => {
    const warnings = [];
    existingRecipe.forEach((ing) => {
      const store = storeById[ing.storeItemId];
      if (!store) return;
      const stock = Number(store.currentStock ?? store.CurrentStock ?? 0);
      const needed = Number(ing.consumptionQty || 0);
      if (stock <= 0) {
        warnings.push({
          name: store.storeName || store.StoreName,
          msg: 'Out of stock — orders will be blocked / clamped to 0',
        });
      } else if (stock < needed * 5) {
        warnings.push({
          name: store.storeName || store.StoreName,
          msg: `Low stock (~${Math.floor(stock / needed)} servings left)`,
        });
      }
    });
    return warnings;
  }, [existingRecipe, storeById]);

  // ─── Variants for the picked parent menu item ──────────────────────────
  const variantsForSelectedMenu = useMemo(() => {
    if (!selectedMenuId) return [];
    return subcategories.filter(
      (s) =>
        String(s.parentItemId ?? s.ParentItemId ?? s.itemId) ===
        String(selectedMenuId),
    );
  }, [selectedMenuId, subcategories]);

  // ─── Render ────────────────────────────────────────────────────────────
  if (loading)
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress />
      </Box>
    );

  const pickedMenu = menuById[selectedMenuId];
  const pickedVariant = subById[selectedVariantId];
  const subjectLabel =
    scope === 'menu'
      ? pickedMenu?.name || 'Pick a menu item above'
      : pickedVariant?.subcategoryName ||
        pickedVariant?.SubcategoryName ||
        'Pick a variant above';

  return (
    <Container maxWidth='lg' sx={{ mt: 3, mb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 3,
          justifyContent: 'center',
        }}>
        <MenuBookIcon sx={{ fontSize: 32, color: '#1565c0' }} />
        <Typography variant='h4' fontWeight='bold'>
          Recipe Master
        </Typography>
      </Box>

      {/* Step 1 — Build the recipe (single mode — always create new) */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          {mode === 'new' ? (
            <>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 2 }}>
                Step 1 — Build the recipe
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='Recipe Name *'
                    size='small'
                    fullWidth
                    value={newRecipe.name}
                    onChange={(e) =>
                      setNewRecipe({ ...newRecipe, name: e.target.value })
                    }
                    placeholder='e.g. Cocktail Standard 60ml'
                    helperText='This name will appear in Create Order'
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label='Category *'
                    size='small'
                    fullWidth
                    value={newRecipe.categoryId}
                    onChange={(e) =>
                      setNewRecipe({
                        ...newRecipe,
                        categoryId: e.target.value,
                      })
                    }
                    helperText='The recipe will appear under this category'>
                    <MenuItem value=''>Select category</MenuItem>
                    {categories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.category_name || c.categoryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label='Selling Price *'
                    type='number'
                    size='small'
                    fullWidth
                    value={newRecipe.sellingPrice}
                    onChange={(e) =>
                      setNewRecipe({
                        ...newRecipe,
                        sellingPrice: e.target.value,
                      })
                    }
                    placeholder='Inc. GST'
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label='GST %'
                    type='number'
                    size='small'
                    fullWidth
                    value={newRecipe.gstPercent}
                    onChange={(e) =>
                      setNewRecipe({
                        ...newRecipe,
                        gstPercent: e.target.value,
                      })
                    }
                    placeholder='e.g. 5, 18'
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label='Department/Printer *'
                    size='small'
                    fullWidth
                    value={newRecipe.department}
                    onChange={(e) =>
                      setNewRecipe({
                        ...newRecipe,
                        department: e.target.value,
                      })
                    }>
                    {departments.map((d) => (
                      <MenuItem
                        key={d.id ?? d.Id}
                        value={d.printername || d.Printername || ''}>
                        {d.department || d.Department}
                        {d.printername || d.Printername
                          ? ` — ${d.printername || d.Printername}`
                          : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label='HSN Code'
                    size='small'
                    fullWidth
                    value={newRecipe.hsnCode}
                    onChange={(e) =>
                      setNewRecipe({
                        ...newRecipe,
                        hsnCode: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>

              {newRecipe.name && newRecipe.categoryId && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Recipe: ${newRecipe.name}`}
                    color='primary'
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    label={`Category: ${categories.find((c) => String(c.id) === String(newRecipe.categoryId))?.category_name || '—'}`}
                    variant='outlined'
                    size='small'
                  />
                  {newRecipe.sellingPrice && (
                    <Chip
                      label={`Price: ${Companycurrency}${newRecipe.sellingPrice}`}
                      variant='outlined'
                      size='small'
                      color='success'
                    />
                  )}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 2 }}>
                Step 1 — Pick existing menu item to edit recipe
              </Typography>

              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='Recipe Name'
                    size='small'
                    fullWidth
                    value={recipeName}
                    onChange={(e) => setRecipeName(e.target.value)}
                    placeholder='e.g. Cocktail Standard 60ml'
                    helperText='Override the recipe display name'
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={menuItems}
                    getOptionLabel={(opt) => opt?.name || ''}
                    value={pickedMenu || null}
                    onChange={(_, v) => {
                      setSelectedMenuId(v?.id ?? '');
                      setSelectedVariantId('');
                    }}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label='Menu Item *'
                        size='small'
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {variantsForSelectedMenu.length > 0 && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={scope === 'variant'}
                            onChange={(e) =>
                              setScope(e.target.checked ? 'variant' : 'menu')
                            }
                          />
                        }
                        label='Per-variant'
                      />
                    </Grid>
                    {scope === 'variant' && (
                      <Grid item xs={12} md={9}>
                        <TextField
                          select
                          size='small'
                          fullWidth
                          label='Variant'
                          value={selectedVariantId}
                          onChange={(e) =>
                            setSelectedVariantId(e.target.value)
                          }>
                          {variantsForSelectedMenu.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                              {v.subcategoryName || v.SubcategoryName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </>
          )}

          {mode === 'edit' && pickedMenu && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}>
              <Chip
                label={
                  recipeName ? `Recipe: ${recipeName}` : 'Recipe (unnamed)'
                }
                color='primary'
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label={`Applies to: ${subjectLabel}`}
                variant='outlined'
                size='small'
              />
              <Chip
                label={`${existingRecipe.length} ingredient${existingRecipe.length === 1 ? '' : 's'}`}
                variant='outlined'
                size='small'
              />
              {stockWarnings.length > 0 && (
                <Chip
                  icon={<WarningAmberIcon />}
                  label={`${stockWarnings.length} stock warning${stockWarnings.length === 1 ? '' : 's'}`}
                  color='warning'
                  size='small'
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — existing recipe (only when editing an existing menu item) */}
      {mode === 'edit' && (selectedMenuId || selectedVariantId) && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                mb: 1,
                flexWrap: 'wrap',
              }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Step 2 — Current Recipe
              </Typography>
              {recipeName && (
                <Typography
                  variant='body2'
                  sx={{ color: '#1565c0', fontWeight: 600 }}>
                  &laquo; {recipeName} &raquo;
                </Typography>
              )}
            </Box>
            {existingRecipe.length === 0 ? (
              <Typography color='text.secondary' fontSize={14}>
                No ingredients yet. Add some below to start the recipe.
              </Typography>
            ) : (
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Ingredient</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Per Order
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Current Stock
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {existingRecipe.map((ing) => {
                    const store = storeById[ing.storeItemId];
                    const stock = Number(
                      store?.currentStock ?? store?.CurrentStock ?? 0,
                    );
                    const needed = Number(ing.consumptionQty || 0);
                    const lowStock =
                      stock <= 0 || (needed > 0 && stock < needed * 3);
                    return (
                      <TableRow key={ing.id}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {store?.storeName || store?.StoreName || '—'}
                        </TableCell>
                        <TableCell align='right'>{needed}</TableCell>
                        <TableCell>{ing.unit}</TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={`${stock} ${store?.unit || store?.Unit || ''}`}
                            color={lowStock ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {ing.activeStatus !== false ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              size='small'
                              color='success'
                              label='Active'
                            />
                          ) : (
                            <Chip
                              size='small'
                              color='default'
                              label='Inactive'
                            />
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip
                            title={
                              ing.activeStatus !== false
                                ? 'Disable (won’t deduct on next order)'
                                : 'Enable'
                            }>
                            <Switch
                              checked={ing.activeStatus !== false}
                              onChange={() => toggleIngredient(ing.id)}
                              size='small'
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {stockWarnings.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {stockWarnings.map((w, i) => (
                  <Alert
                    key={i}
                    severity='warning'
                    icon={<WarningAmberIcon />}
                    sx={{ mb: 0.5 }}>
                    <b>{w.name}</b> — {w.msg}
                  </Alert>
                ))}
              </Box>
            )}

            {recipeCost > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#f0f7ff',
                  display: 'inline-block',
                }}>
                <Typography variant='caption' color='text.secondary'>
                  Approx Recipe Cost
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#1565c0' }}>
                  {Companycurrency}
                  {recipeCost.toFixed(2)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2/3 — add ingredients
          • New mode: always visible (this IS the recipe being built)
          • Edit mode: visible only after a menu item is picked */}
      {(mode === 'new' || selectedMenuId || selectedVariantId) && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 2 }}>
              {mode === 'new'
                ? 'Step 2 — Add Ingredients'
                : 'Step 3 — Add Ingredients'}
            </Typography>

            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                  <TableCell sx={{ fontWeight: 700, width: '40%' }}>
                    Store Item
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Qty per Order</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current Stock</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {draftIngredients.map((d, i) => {
                  const stockNow = d.storeItem
                    ? Number(
                        d.storeItem.currentStock ??
                          d.storeItem.CurrentStock ??
                          0,
                      )
                    : null;
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <Autocomplete
                          options={storeItems}
                          getOptionLabel={(opt) =>
                            opt?.storeName || opt?.StoreName || ''
                          }
                          value={d.storeItem || null}
                          onChange={(_, v) =>
                            updateDraft(i, {
                              storeItem: v,
                              storeItemId: v?.id ?? v?.Id ?? null,
                              unit: v?.unit || v?.Unit || d.unit,
                            })
                          }
                          renderOption={(props, opt) => (
                            <li {...props} key={opt.id ?? opt.Id}>
                              <Box>
                                <Typography fontSize={14} fontWeight={600}>
                                  {opt.storeName || opt.StoreName}
                                </Typography>
                                <Typography
                                  fontSize={11}
                                  color='text.secondary'>
                                  Stock:{' '}
                                  {Number(
                                    opt.currentStock ?? opt.CurrentStock ?? 0,
                                  )}{' '}
                                  {opt.unit || opt.Unit}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderInput={(p) => (
                            <TextField
                              {...p}
                              size='small'
                              placeholder='Pick ingredient'
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 110 }}>
                        <TextField
                          size='small'
                          type='number'
                          inputProps={{ min: 0, step: 'any' }}
                          value={d.qty}
                          onChange={(e) =>
                            updateDraft(i, { qty: e.target.value })
                          }
                          placeholder='e.g. 30'
                        />
                      </TableCell>
                      <TableCell sx={{ width: 110 }}>
                        <TextField
                          select
                          size='small'
                          value={d.unit}
                          onChange={(e) =>
                            updateDraft(i, { unit: e.target.value })
                          }>
                          {UNITS.map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {stockNow != null ? (
                          <Chip
                            size='small'
                            label={`${stockNow} ${d.storeItem?.unit || d.storeItem?.Unit || ''}`}
                            color={stockNow <= 0 ? 'error' : 'default'}
                          />
                        ) : (
                          <Typography fontSize={12} color='text.secondary'>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() => removeDraftRow(i)}>
                          <DeleteIcon fontSize='small' color='error' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                startIcon={<AddIcon />}
                onClick={addDraftRow}
                variant='outlined'>
                Add Ingredient
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant='contained'
                onClick={saveRecipe}
                disabled={saving}
                size='large'>
                {saving
                  ? 'Saving...'
                  : mode === 'new'
                    ? 'Create Recipe & Menu Item'
                    : 'Save Recipe'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant='caption' color='text.secondary'>
              {mode === 'new' ? (
                <>
                  <b>Example:</b> Recipe name = "Cocktail Standard 60ml",
                  Category = "Liquor", Price = ₹900, Ingredients = 30ml CockTail +
                  30ml Blanders + 30ml 8PM + 30ml Signature. As soon as you save,
                  this "Cocktail Standard 60ml" item will appear in the "Liquor"
                  category in Create Order, and on ordering all ingredients will
                  auto-deduct.
                </>
              ) : (
                <>
                  <b>Example:</b> Red Label Cocktail · 30 ml Red Label · 30 ml
                  Vodka · 100 ml Soda · 4 pcs Ice Cubes. Customer ordering 2 of
                  this cocktail will auto-deduct 60 ml Red Label, 60 ml Vodka,
                  200 ml Soda, 8 pcs Ice Cubes from store stock.
                </>
              )}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ─── All Recipes List ──────────────────────────────────────────────
          Groups all active ItemStoreMapping rows by their menu item so the
          user can see what they've built so far. Click an "Edit" button to
          load that recipe back into the form for re-editing. */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
              All Recipes Created So Far
            </Typography>
            <Chip
              label={`${
                new Set(
                  allMappings
                    .filter((m) => m.activeStatus !== false)
                    .map((m) => `${m.itemId}-${m.subcategoryId || 0}`),
                ).size
              } recipes`}
              size='small'
              color='primary'
            />
          </Box>

          {(() => {
            // Group active mappings by ItemId + SubcategoryId
            const groups = new Map();
            allMappings
              .filter((m) => m.activeStatus !== false)
              .forEach((m) => {
                const key = `${m.itemId}-${m.subcategoryId || 0}`;
                if (!groups.has(key)) {
                  groups.set(key, {
                    itemId: m.itemId,
                    subcategoryId: m.subcategoryId || 0,
                    isSubcategory: m.isSubcategory,
                    recipeName: m.recipeName,
                    ingredients: [],
                  });
                }
                groups.get(key).ingredients.push(m);
              });
            const recipeList = Array.from(groups.values());

            if (recipeList.length === 0) {
              return (
                <Typography color='text.secondary' fontSize={14}>
                  No recipe created yet. Fill in the form above and click "Create
                  Recipe & Menu Item".
                </Typography>
              );
            }

            return (
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Recipe Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Menu Item</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Ingredients
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipeList.map((r, i) => {
                    const menu = menuById[r.itemId];
                    const variant = subById[r.subcategoryId];
                    const displayName =
                      r.recipeName ||
                      (variant
                        ? variant.subcategoryName || variant.SubcategoryName
                        : menu?.name) ||
                      '(unnamed)';
                    return (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>
                          {displayName}
                        </TableCell>
                        <TableCell>
                          {menu?.name || '—'}
                          {variant && (
                            <Typography
                              variant='caption'
                              display='block'
                              color='text.secondary'>
                              variant:{' '}
                              {variant.subcategoryName ||
                                variant.SubcategoryName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          <Chip
                            size='small'
                            label={`${r.ingredients.length} item${r.ingredients.length === 1 ? '' : 's'}`}
                            color='success'
                            variant='outlined'
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {r.ingredients
                            .slice(0, 3)
                            .map((ing) => {
                              const store = storeById[ing.storeItemId];
                              return `${store?.storeName || '?'} ${ing.consumptionQty}${ing.unit}`;
                            })
                            .join(', ')}
                          {r.ingredients.length > 3 &&
                            ` +${r.ingredients.length - 3} more`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
